import { useCallback, useEffect, useMemo, useState } from "react";
import Sidebar from "@/components/schengen/Sidebar";
import TopBar from "@/components/schengen/TopBar";
import MetricsPanel from "@/components/schengen/MetricsPanel";
import AddTripForm from "@/components/schengen/AddTripForm";
import TripsList from "@/components/schengen/TripsList";
import Timeline180 from "@/components/schengen/Timeline180";
import CalendarView from "@/components/schengen/CalendarView";
import ExplanationPanel from "@/components/schengen/ExplanationPanel";
import Disclaimer from "@/components/schengen/Disclaimer";
import ActionsBar from "@/components/schengen/ActionsBar";
import YearSelector from "@/components/schengen/YearSelector";
import EditTripDialog from "@/components/schengen/EditTripDialog";
import Footer from "@/components/schengen/Footer";
import AdminPanel from "@/pages/AdminPanel";
import { calculate } from "@/lib/schengenCalc";
import { todayISO } from "@/lib/dateUtils";
import { buildSample } from "@/lib/samples";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { useT } from "@/lib/i18n";
import { track } from "@/lib/analytics";
import { toast } from "sonner";

export default function SchengenCalculator() {
  const { user: me } = useAuth();
  const { t } = useT();
  const [view, setView] = useState("calc"); // 'calc' | 'admin'
  const [defaultYear, setDefaultYear] = useState(() => new Date().getFullYear());
  const [mobileSidebarOpen, setMobileSidebarOpen] = useState(false);

  const [users, setUsers] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [selectedCompanyId, setSelectedCompanyId] = useState(null);
  const [viewedUserId, setViewedUserId] = useState(null);
  const [trips, setTrips] = useState([]);
  const [tripsLoading, setTripsLoading] = useState(true);
  const [editing, setEditing] = useState(null);

  const loadUsers = useCallback(async () => {
    try {
      const list = await api.get("/api/users");
      setUsers(list);
      if (!viewedUserId && me) setViewedUserId(me.id);
    } catch (err) {
      toast.error(err.message);
    }
  }, [me, viewedUserId]);

  const loadCompanies = useCallback(async () => {
    if (!me) return;
    try {
      const list = await api.get("/api/companies");
      setCompanies(list || []);
    } catch {
      /* super_user without company may 200 with []; ignore errors */
    }
  }, [me]);

  useEffect(() => {
    if (!me) return;
    loadUsers();
    loadCompanies();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [me]);

  useEffect(() => {
    if (view === "calc" && me) {
      loadUsers();
      loadCompanies();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [view]);

  const loadTrips = useCallback(async () => {
    if (!viewedUserId) return;
    setTripsLoading(true);
    try {
      const list = await api.get(`/api/users/${viewedUserId}/trips`);
      setTrips(list.map((t) => ({ id: t.id, entry: t.entry, exit: t.exit })));
    } catch (err) {
      toast.error(err.message);
      setTrips([]);
    } finally {
      setTripsLoading(false);
    }
  }, [viewedUserId]);

  useEffect(() => {
    loadTrips();
  }, [loadTrips]);

  const today = useMemo(() => todayISO(), []);
  const result = useMemo(() => calculate(trips, today), [trips, today]);

  const addTrip = async (t) => {
    if (!viewedUserId) return;
    try {
      await api.post(`/api/users/${viewedUserId}/trips`, {
        entry: t.entry,
        exit: t.exit,
      });
      track("add_trip", { open: !t.exit });
      await loadTrips();
    } catch (err) {
      toast.error(err.message);
    }
  };
  const deleteTrip = async (id) => {
    try {
      await api.del(`/api/trips/${id}`);
      track("delete_trip");
      await loadTrips();
    } catch (err) {
      toast.error(err.message);
    }
  };
  const saveEdit = async (entry, exitVal) => {
    if (!editing) return;
    await api.patch(`/api/trips/${editing.id}`, { entry, exit: exitVal });
    track("edit_trip");
    await loadTrips();
  };
  const closeTrip = async (trip, exitDate) => {
    try {
      await api.patch(`/api/trips/${trip.id}`, { entry: trip.entry, exit: exitDate });
      track("close_trip");
      toast.success(t("trips.closedOk", { d: exitDate }));
      await loadTrips();
    } catch (err) {
      toast.error(err.message);
    }
  };
  const resetAll = async () => {
    if (!viewedUserId) return;
    try {
      await api.post(`/api/users/${viewedUserId}/trips/bulk`, {
        trips: [],
        replace: true,
      });
      await loadTrips();
    } catch (err) {
      toast.error(err.message);
    }
  };
  const loadSample = async () => {
    if (!viewedUserId) return;
    try {
      const sample = buildSample().map((t) => ({ entry: t.entry, exit: t.exit }));
      await api.post(`/api/users/${viewedUserId}/trips/bulk`, {
        trips: sample,
        replace: true,
      });
      await loadTrips();
    } catch (err) {
      toast.error(err.message);
    }
  };
  const importCSVTrips = async (parsed) => {
    if (!viewedUserId) return;
    try {
      await api.post(`/api/users/${viewedUserId}/trips/bulk`, {
        trips: parsed.map((t) => ({ entry: t.entry, exit: t.exit })),
        replace: true,
      });
      await loadTrips();
    } catch (err) {
      toast.error(err.message);
    }
  };

  const viewedUser = users.find((u) => u.id === viewedUserId);
  const activeCompanyId = me?.role === "admin"
    ? (selectedCompanyId || viewedUser?.company_id)
    : me?.company_id;
  const activeCompany = companies.find((c) => c.id === activeCompanyId) || null;

  const sidebar = (
    <Sidebar
      users={users}
      companies={companies}
      currentUserId={viewedUserId}
      onSelectUser={setViewedUserId}
      currentCompanyId={selectedCompanyId}
      onSelectCompany={setSelectedCompanyId}
      onOpenAdmin={() => setView("admin")}
      mobileOpen={mobileSidebarOpen}
      onMobileOpenChange={setMobileSidebarOpen}
    />
  );

  return (
    <div className="min-h-screen flex bg-background">
      {sidebar}
      <div className="flex-1 flex flex-col min-w-0">
        <TopBar onToggleSidebar={() => setMobileSidebarOpen(true)} />

        {view === "admin" ? (
          <div className="flex-1">
            <AdminPanel onBack={() => setView("calc")} />
          </div>
        ) : (
          <main className="flex-1 mx-auto w-full max-w-6xl px-3 sm:px-4 md:px-8 py-4 sm:py-6 md:py-10 space-y-5 sm:space-y-6">
            <div className="flex items-center justify-between gap-3 flex-wrap">
              <h2
                className="font-serif-display text-2xl sm:text-3xl text-white tracking-tight"
                data-testid="page-title"
              >
                {t("tabs.today")}
              </h2>
              <YearSelector year={defaultYear} onChange={setDefaultYear} />
            </div>

            {viewedUser && me && viewedUser.id !== me.id && (
              <div
                className="rounded-lg border border-sky-500/30 bg-sky-500/10 px-4 py-2 text-xs text-sky-200"
                data-testid="viewing-other-banner"
              >
                {t("viewOther.banner", { u: viewedUser.username })}
              </div>
            )}

            <MetricsPanel result={result} />

            <div className="grid grid-cols-1 xl:grid-cols-12 gap-6 items-stretch">
              <div className="xl:col-span-7 flex">
                <Timeline180 result={result} trips={trips} />
              </div>
              <div className="xl:col-span-5 flex">
                <AddTripForm onAdd={addTrip} defaultYear={defaultYear} />
              </div>
              <div className="xl:col-span-7 flex">
                <CalendarView trips={trips} result={result} />
              </div>
              <div className="xl:col-span-5 flex">
                <TripsList
                  trips={trips}
                  result={result}
                  onDelete={deleteTrip}
                  onEdit={setEditing}
                  onClose={closeTrip}
                  loading={tripsLoading}
                />
              </div>
            </div>

            <ActionsBar
              trips={trips}
              result={result}
              company={activeCompany}
              user={viewedUser || me}
              onLoadSample={loadSample}
              onReset={resetAll}
              onImport={importCSVTrips}
            />

            <ExplanationPanel />
            <Disclaimer />
          </main>
        )}

        <EditTripDialog
          open={!!editing}
          trip={editing}
          onSave={saveEdit}
          onClose={() => setEditing(null)}
          defaultYear={defaultYear}
        />

        <Footer />
      </div>
    </div>
  );
}
